import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Modal, Select, Spin, Alert, Button, message, Descriptions, Tabs } from 'antd';
import { schedulerClient } from '../../api/scheduler-client';

interface RunPipelineDialogProps {
  open: boolean;
  onClose: () => void;
  pipelineFile?: string;
}

const PROJECT_PATH = '/project-export.zip'; // For rollback: frontend sends project file
const DEFAULT_PROJECT_NAME = 'project-test'; // Option B: backend builds zip from project-home
const FALLBACK_FILE = 'transforms/switch-case-basic.hpl';

/** Set to true to use Option B (backend builds project zip). Set to false to rollback to frontend-sent project. */
const USE_PROJECT_FROM_BACKEND = true;

const LOG_LEVELS = [
  'Nothing',
  'Error',
  'Minimal',
  'Basic',
  'Detail',
  'Debug',
  'Rowlevel',
] as const;

const RunPipelineDialog: React.FC<RunPipelineDialogProps> = ({ open, onClose, pipelineFile }) => {
  const [runTargets, setRunTargets] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<string | undefined>(undefined);
  const [logLevel, setLogLevel] = useState<string>('Basic');
  const [running, setRunning] = useState(false);
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [status, setStatus] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>('status');
  const [logs, setLogs] = useState<string>('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const logsPollingRef = useRef<NodeJS.Timeout | null>(null);
  const finalLogFetchRef = useRef<boolean>(false); // To ensure one last log fetch

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setStatus(null);
    setExecutionId(null);
    setLogs('');
    setLogsError(null);
    setLogsLoading(false);
    setActiveTab('status');
    finalLogFetchRef.current = false;

    // Fetch run targets using the scheduler client
    schedulerClient.getRunTargets()
      .then((names) => {
        setRunTargets(names);
        setLoading(false);
        if (names.length > 0) {
          setSelectedTarget(names[0]); // Select first target by default
        }
      })
      .catch((err) => {
        setError(err.message || 'Unknown error');
        setLoading(false);
      });

    // Cleanup polling on dialog close
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (logsPollingRef.current) clearInterval(logsPollingRef.current);
    };
  }, [open]);

  // Helper to get the real project zip file from public directory
  const getProjectFile = async (): Promise<File> => {
    try {
      const response = await fetch(PROJECT_PATH);
      if (!response.ok) throw new Error('Failed to fetch project zip from public directory');
      const blob = await response.blob();
      return new File([blob], 'project-export.zip', { type: 'application/zip' });
    } catch (err) {
      throw new Error('Could not load project zip file. Please ensure project-export.zip is in the public directory.');
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setStatus(null);
    setError(null);
    setExecutionId(null);
    setLogs('');
    setLogsError(null);
    setLogsLoading(false);
    finalLogFetchRef.current = false;

    try {
      const runRequest: Parameters<typeof schedulerClient.runPipeline>[0] = {
        type: 'non-published',
        file: pipelineFile || FALLBACK_FILE,
        runTarget: selectedTarget || '',
        logLevel,
        runConfig: 'local',
      };
      if (USE_PROJECT_FROM_BACKEND) {
        runRequest.projectName = DEFAULT_PROJECT_NAME;
      } else {
        runRequest.project = await getProjectFile();
      }

      const executionId = await schedulerClient.runPipeline(runRequest);

      setExecutionId(executionId);
      setStatus({ status: 'STARTED', id: executionId });
      message.success('Pipeline run started successfully');
      // Start polling for status
      pollingRef.current = setInterval(async () => {
        try {
          const statusData = await schedulerClient.getExecutionStatus(executionId);
          setStatus(statusData);
          
          if (statusData.status && schedulerClient.isTerminalState(statusData.status)) {
            if (pollingRef.current) clearInterval(pollingRef.current);
            if (logsPollingRef.current) clearInterval(logsPollingRef.current);
            setRunning(false);
            // Trigger one last log fetch after status polling stops
            if (!finalLogFetchRef.current) {
              fetchLogs(executionId);
              finalLogFetchRef.current = true;
            }
          }
        } catch (err: any) {
          setError('Failed to fetch execution status: ' + (err.message || err));
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (logsPollingRef.current) clearInterval(logsPollingRef.current);
          setRunning(false);
          if (!finalLogFetchRef.current) {
            fetchLogs(executionId);
            finalLogFetchRef.current = true;
          }
        }
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      message.error('Failed to start pipeline run');
      setRunning(false);
    }
  };

  // Helper to determine if status is terminal
  const isTerminalStatus = (statusValue: string | undefined) => {
    return statusValue && schedulerClient.isTerminalState(statusValue);
  };

  const fetchLogs = useCallback(async (id: string) => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const data = await schedulerClient.getExecutionLogs(id);
      setLogs(data);
    } catch (err: any) {
      setLogsError(err.message || 'Unknown error');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  // Poll logs when logs tab is active and executionId is present and status polling is active
  useEffect(() => {
    if (activeTab !== 'logs' || !executionId || isTerminalStatus(status?.status)) {
      if (logsPollingRef.current) clearInterval(logsPollingRef.current);
      return;
    }
    fetchLogs(executionId);
    logsPollingRef.current = setInterval(() => fetchLogs(executionId), 2000);
    return () => {
      if (logsPollingRef.current) clearInterval(logsPollingRef.current);
    };
  }, [activeTab, executionId, status, fetchLogs]);

  // After status polling stops, fetch logs one last time
  useEffect(() => {
    if (!executionId) return;
    if (isTerminalStatus(status?.status)) {
      // Only fetch if logs are not already up to date
      fetchLogs(executionId);
    }
    // No cleanup needed
  }, [executionId, status, fetchLogs]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (logsPollingRef.current) clearInterval(logsPollingRef.current);
    };
  }, []);

  const isSubmitting = running && !executionId;

  // Helper to render status fields in a table
  const renderStatusTable = (statusObj: any) => {
    if (!statusObj) return null;
    // Pick common fields, but show all top-level fields if present
    const fields = [
      'status',
      'id',
      'startTime',
      'endTime',
      'duration',
      'error',
      'message',
      'result',
      'logLevel',
      'runTarget',
      'pipelineName',
    ];
    const items = fields
      .filter((key) => key in statusObj)
      .map((key) => ({ key, value: statusObj[key] }));
    // Add any other top-level fields not in the above list
    Object.keys(statusObj).forEach((key) => {
      if (!fields.includes(key)) {
        items.push({ key, value: statusObj[key] });
      }
    });
    
    // Calculate duration if creationDateTime and finishedDateTime are present
    if (statusObj.creationDateTime && statusObj.finishedDateTime) {
      try {
        const start = new Date(statusObj.creationDateTime);
        const end = new Date(statusObj.finishedDateTime);
        const diffMs = end.getTime() - start.getTime();
        const diffSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(diffSeconds / 3600);
        const minutes = Math.floor((diffSeconds % 3600) / 60);
        const seconds = diffSeconds % 60;
        
        let durationStr = '';
        if (hours > 0) durationStr += `${hours}h `;
        if (minutes > 0) durationStr += `${minutes}m `;
        durationStr += `${seconds}s`;
        
        items.push({ key: 'duration', value: durationStr.trim() });
      } catch (err) {
        // Ignore date parsing errors
      }
    }
    
    return (
      <Descriptions bordered size="small" column={1} style={{ marginTop: 16 }}>
        {items.map((item) => (
          <Descriptions.Item key={item.key} label={item.key}>
            {typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    );
  };

  // Helper to render log fields in a table and log text below
  const renderLogsTable = (logsData: string) => {
    if (!logsData) return null;
    let parsed: any = null;
    try {
      parsed = JSON.parse(logsData);
    } catch {
      // Not JSON, show as plain text
      return (
        <pre style={{ maxHeight: 300, overflow: 'auto', padding: 12, border: '1px solid #d9d9d9', borderRadius: 4 }}>
          {logsData}
        </pre>
      );
    }
    // Separate log field from the rest
    const { log, ...rest } = parsed;
    const items = Object.keys(rest).map((key) => ({ key, value: rest[key] }));
    // Table for metadata
    const metaTable = (
      <Descriptions bordered size="small" column={1} style={{ marginTop: 16 }}>
        {items.map((item) => (
          <Descriptions.Item key={item.key} label={item.key}>
            {typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    );
    // Parse log lines if possible
    let logLines: any[] = [];
    if (log) {
      let logJson: any = log;
      if (typeof log === 'string') {
        try {
          logJson = JSON.parse(log);
        } catch {
          // Not JSON, show as plain text
          return (
            <>
              {metaTable}
              <pre style={{ maxHeight: 300, overflow: 'auto', padding: 12, border: '1px solid #d9d9d9', borderRadius: 4, marginTop: 16 }}>
                {log}
              </pre>
            </>
          );
        }
      }
      if (Array.isArray(logJson)) {
        logLines = logJson;
      }
    }
    return (
      <>
        {metaTable}
        {log === undefined ? (
          <div style={{ marginTop: 16, color: '#888' }}>No log field in log result.</div>
        ) : logLines.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <span style={{ fontWeight: 600 }}>Log Output</span>
            <div style={{ maxHeight: 300, overflow: 'auto', marginTop: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
              <table style={{ width: '100%' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa' }}>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #d9d9d9' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #d9d9d9' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #d9d9d9' }}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {logLines.map((line, idx) => (
                    <tr key={line.logLineNr || idx}>
                      <td style={{ padding: '8px', verticalAlign: 'top', borderBottom: '1px solid #f0f0f0' }}>{line.logLineNr ?? idx}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top', borderBottom: '1px solid #f0f0f0' }}>{line.logDate ?? ''}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top', whiteSpace: 'pre-wrap', borderBottom: '1px solid #f0f0f0' }}>{line.message ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 16, color: '#888' }}>No log lines available.</div>
        )}
      </>
    );
  };

  return (
    <Modal
      open={open}
      title="Run Pipeline"
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose} disabled={running}>Close</Button>,
        <Button
          key="run"
          type="primary"
          onClick={handleRun}
          disabled={!selectedTarget || running}
          loading={running}
        >
          Run
        </Button>,
      ]}
      destroyOnClose
    >
      {loading ? (
        <Spin tip="Loading run targets..." />
      ) : error ? (
        <Alert type="error" message={error} />
      ) : (
        <>
          <Select
            style={{ width: '100%', marginBottom: 16 }}
            placeholder="Select a run target"
            value={selectedTarget}
            onChange={setSelectedTarget}
            disabled={isSubmitting}
          >
            {runTargets.map((name) => (
              <Select.Option key={name} value={name}>
                {name}
              </Select.Option>
            ))}
          </Select>
          <Select
            style={{ width: '100%' }}
            placeholder="Select log level"
            value={logLevel}
            onChange={setLogLevel}
            disabled={isSubmitting}
          >
            {LOG_LEVELS.map((level) => (
              <Select.Option key={level} value={level}>
                {level}
              </Select.Option>
            ))}
          </Select>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            style={{ marginTop: 16 }}
            items={[
              {
                key: 'status',
                label: 'Status & Metrics',
                children: status && renderStatusTable(status),
              },
              {
                key: 'logs',
                label: 'Logs',
                children: logsLoading ? (
                  <Spin tip="Loading logs..." />
                ) : logsError ? (
                  <Alert type="error" message={logsError} />
                ) : (
                  renderLogsTable(logs)
                ),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default RunPipelineDialog;